/**
 * useGoalDeletionWithUndo — Soft-deletes a single goal via the `archived_at`
 * column and exposes an Undo affordance through a sonner toast action.
 *
 * Why soft-delete: hard deletion is destructive and irreversible. The goals
 * table already supports soft-delete (`archived_at`) and the main goal fetch
 * filters archived rows out, so flipping that flag is functionally identical
 * to deletion from the user's perspective while leaving a clean undo path.
 *
 * The user can also recover the goal later from the Archive page if the toast
 * is missed.
 */
import { useCallback } from "react";
import { toast } from "sonner";
import { Goal } from "@/types/todo";
import { useBulkGoalActions } from "@/hooks/useBulkGoalActions";
import { logger } from "@/utils/logger";

export const useGoalDeletionWithUndo = () => {
  const { archiveGoals, restoreGoals } = useBulkGoalActions();

  const deleteGoalWithUndo = useCallback(
    async (goalToDelete: Goal) => {
      if (!goalToDelete?.id) return;

      try {
        const count = await archiveGoals([goalToDelete.id]);
        if (count === 0) {
          toast.error("Could not delete goal", {
            description: "Please try again.",
          });
          return;
        }

        toast.success("Goal deleted", {
          description: `"${goalToDelete.title}" was removed. Linked rewards are preserved.`,
          duration: 6000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await restoreGoals([goalToDelete.id]);
                toast.success("Goal restored", {
                  description: `"${goalToDelete.title}" is back in your list.`,
                  duration: 3000,
                });
              } catch (err) {
                logger.error("[useGoalDeletionWithUndo] Restore failed:", err);
                toast.error("Could not undo deletion", {
                  description: "You can recover it from the Archive page.",
                });
              }
            },
          },
        });
      } catch (error: any) {
        logger.error("[useGoalDeletionWithUndo] Delete failed:", error);
        toast.error("Error deleting goal", {
          description: error?.message ?? "Please try again.",
        });
      }
    },
    [archiveGoals, restoreGoals]
  );

  return { deleteGoalWithUndo };
};
